import axios from 'axios';
import { getAuthToken } from '../utils/authUtils'; //

// Backend API configuration
// Use the standard billing API base URL
const API_BASE_URL = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || 'https://quantumleap-emr-dev.web.app';

const BILLING_CLAIMS_ENDPOINT = `${API_BASE_URL}/billing`;

// Helper function to get authentication headers
const getAuthHeaders = async () => {
  const token = await getAuthToken();
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

class ClearinghouseService {
  constructor() {
    this.isInitialized = false;
  }

  /**
   * Initialize clearinghouse connection (now handled by backend)
   * @param {string} provider - 'OFFICE_ALLY' or 'OPTUM'
   * @param {Object} credentials - Provider-specific credentials
   */
  async initialize(provider, credentials) {
    try {
      // Save configuration to backend
      const headers = await getAuthHeaders();
      const response = await axios.post(`${BILLING_CLAIMS_ENDPOINT}/claims/initialize`, {
        provider,
        credentials
      }, { headers });

      if (response.data.success) {
        this.isInitialized = true;
        console.log(`Successfully initialized ${provider} clearinghouse`);
        return true;
      }
      
      throw new Error(response.data.message || 'Failed to initialize clearinghouse');
    } catch (error) {
      console.error('Failed to initialize clearinghouse:', error);
      throw error;
    }
  }

  /**
   * Test connection to clearinghouse (using backend)
   */
  async testConnection() {
    try {
      const headers = await getAuthHeaders();
      // Test connection by checking configuration status
      const response = await axios.get(`${BILLING_CLAIMS_ENDPOINT}/claims`, { headers });
      
      return response.data;
    } catch (error) {
      console.error('Connection test failed:', error);
      throw new Error('Failed to test clearinghouse connection');
    }
  }

  /**
   * Get current configuration status
   */
  async getConfigurationStatus() {
    try {
      const headers = await getAuthHeaders();
      // Get configuration from the configuration collection
      const response = await axios.get(`${API_BASE_URL}/admin/configuration/clearinghouseConfiguration`, { headers });
      
      return response.data;
    } catch (error) {
      console.error('Failed to get configuration status:', error);
      throw new Error('Failed to get configuration status');
    }
  }

  /**
   * Check patient eligibility (using backend API)
   * @param {Object} eligibilityData - Patient and insurance information
   */
  async checkEligibility(eligibilityData) {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.post(`${BILLING_CLAIMS_ENDPOINT}/eligibility`, eligibilityData, { headers });

      return response.data;
    } catch (error) {
      console.error('Eligibility check failed:', error);
      throw new Error('Failed to check patient eligibility');
    }
  }

  /**
   * Submit insurance claim (using backend API)
   * @param {Object} claimData - Claim information including bill and patient data
   */
  async submitClaim(claimData) {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.post(`${BILLING_CLAIMS_ENDPOINT}/submit`, claimData, { headers });

      return response.data;
    } catch (error) {
      console.error('Claim submission failed:', error);
      throw new Error('Failed to submit claim');
    }
  }

  /**
   * Check claim status (using backend API)
   * @param {string} claimNumber - Claim identifier
   */
  async checkClaimStatus(claimNumber) {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(`${BILLING_CLAIMS_ENDPOINT}/status/${claimNumber}`, { headers });

      return response.data;
    } catch (error) {
      console.error('Claim status check failed:', error);
      throw new Error('Failed to check claim status');
    }
  }

  /**
   * Download electronic remittance advice (ERA) (using backend API)
   * @param {Object} dateRange - Date range for remittance download
   */
  async downloadRemittance(dateRange) {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.post(`${BILLING_CLAIMS_ENDPOINT}/remittance`, dateRange, { headers });

      return response.data;
    } catch (error) {
      console.error('Remittance download failed:', error);
      throw new Error('Failed to download remittance');
    }
  }

  /**
   * Download acknowledgments (999 transactions) (using backend API)
   * @param {Object} dateRange - Date range for acknowledgment download
   */
  async downloadAcknowledments(dateRange) {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.post(`${BILLING_CLAIMS_ENDPOINT}/acknowledgments`, dateRange, { headers });

      return response.data;
    } catch (error) {
      console.error('Acknowledgment download failed:', error);
      throw new Error('Failed to download acknowledgments');
    }
  }

  /**
   * Get supported features (static information)
   */
  getSupportedFeatures() {
    return {
      realTimeEligibility: true,
      batchClaims: true,
      electronicRemittance: true,
      claimTracking: true,
      x12EDI: true,
      jsonAPI: true
    };
  }

  /**
   * Reset service state
   */
  disconnect() {
    this.isInitialized = false;
  }
}

export default ClearinghouseService; 